'use client';

import type { DeviceFingerprint } from './types';

/**
 * Generates a simple hash from a string using Web Crypto API.
 * Falls back to a basic hash if crypto is unavailable.
 */
async function hashString(str: string): Promise<string> {
  if (typeof window !== 'undefined' && window.crypto?.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Fallback: simple djb2 hash for environments without crypto
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * Generates a canvas fingerprint by rendering text and shapes.
 * Different GPUs/browsers render slightly differently.
 */
function getCanvasFingerprint(): string | undefined {
  if (typeof document === 'undefined') return undefined;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    // Draw text with specific font
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Canvas', 4, 17);

    // Draw a shape
    ctx.beginPath();
    ctx.arc(50, 25, 15, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fill();

    return canvas.toDataURL();
  } catch {
    return undefined;
  }
}

/**
 * Collects device fingerprint components from the browser.
 */
function collectFingerprintComponents(): DeviceFingerprint {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const screen = typeof window !== 'undefined' ? window.screen : null;

  return {
    screenResolution: screen ? `${screen.width}x${screen.height}` : 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    language: nav?.language || 'unknown',
    platform: nav?.platform || 'unknown',
    colorDepth: screen?.colorDepth || 0,
    deviceMemory: (nav as Navigator & { deviceMemory?: number })?.deviceMemory,
    hardwareConcurrency: nav?.hardwareConcurrency,
    cookiesEnabled: nav?.cookieEnabled ?? false,
    touchSupport: typeof window !== 'undefined' && 'ontouchstart' in window,
    canvasHash: undefined, // Will be set below
  };
}

/**
 * Collects a device fingerprint and returns a hash.
 * This should be called client-side before login.
 *
 * @returns A hash string representing the device fingerprint
 */
export async function collectDeviceFingerprint(): Promise<string> {
  const components = collectFingerprintComponents();

  // Add canvas fingerprint
  const canvasData = getCanvasFingerprint();
  if (canvasData) {
    components.canvasHash = await hashString(canvasData);
  }

  // Create a stable string representation
  const fingerprintString = JSON.stringify({
    sr: components.screenResolution,
    tz: components.timezone,
    lang: components.language,
    plat: components.platform,
    cd: components.colorDepth,
    dm: components.deviceMemory,
    hc: components.hardwareConcurrency,
    ce: components.cookiesEnabled,
    ts: components.touchSupport,
    ch: components.canvasHash,
  });

  // Hash the fingerprint
  return hashString(fingerprintString);
}

/**
 * Gets fingerprint components for debugging/display purposes.
 * Do not send raw components to server - only send the hash.
 */
export function getVisibleFingerprintInfo(): Partial<DeviceFingerprint> {
  const components = collectFingerprintComponents();
  // Return only non-sensitive components for display
  return {
    screenResolution: components.screenResolution,
    timezone: components.timezone,
    platform: components.platform,
  };
}
